import React, { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { toISO as fmt } from '../../utils/formatters'
import { getBizColor } from '../../utils/colors'
import JobTimer from './JobTimer'
import CrewSelection from '../dashboard/CrewSelection'
import { pickRelevantSegment, sortSegments } from '../../lib/segments'
import { resolveDueOnSite } from '../../lib/dueOnSite'

const AUTO_STOP_MS = 12 * 3600000
const CANCEL_WINDOW_MS = 30000

function parseLines(job) {
  if (!job?.bid_lines_json) return []
  try {
    const parsed = typeof job.bid_lines_json === 'string' ? JSON.parse(job.bid_lines_json) : job.bid_lines_json
    return Array.isArray(parsed) ? parsed : []
  } catch (e) { return [] }
}

function money(n) {
  const v = Number(n || 0)
  return '$' + v.toLocaleString('en-US', { maximumFractionDigits: 0 })
}

export default function MyDayPage() {
  const { profile } = useAuth()
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeEntry, setActiveEntry] = useState(null)
  const [todayEntries, setTodayEntries] = useState([])
  const [crew, setCrew] = useState([])
  const [showCrewPicker, setShowCrewPicker] = useState(false)
  const [now, setNow] = useState(Date.now())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const today = fmt(new Date())
  const todayMidnightISO = new Date(today + 'T00:00:00').toISOString()
  const autoStopFiredRef = useRef(false)

  // Inject CSS for pulsing stop button
  useEffect(() => {
    const id = 'job-timer-keyframes'
    if (!document.getElementById(id)) {
      const st = document.createElement('style')
      st.id = id
      st.textContent = '@keyframes jobTimerPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(224,92,92,0.55); } 50% { box-shadow: 0 0 0 8px rgba(224,92,92,0); } } .job-timer-pulse { animation: jobTimerPulse 1.4s ease-in-out infinite; }'
      document.head.appendChild(st)
    }
  }, [])

  const load = useCallback(async () => {
    if (!profile?.id) return
    setLoading(true)
    const [jobsRes, activeRes, entriesRes, crewRes] = await Promise.all([
      supabase.from('jobs').select('*')
        .eq('crew_leader_id', profile.id)
        .not('status', 'in', '(cancelled,completed)')
        .order('sort_position', { ascending: true, nullsFirst: false })
        .order('start_date', { ascending: true }),
      supabase.from('job_time_entries').select('*')
        .eq('user_id', profile.id).is('clock_out', null)
        .order('clock_in', { ascending: false }).limit(1).maybeSingle(),
      supabase.from('job_time_entries').select('*')
        .eq('user_id', profile.id)
        .gte('clock_in', todayMidnightISO)
        .not('clock_out', 'is', null),
      supabase.from('daily_crew_assignments')
        .select('worker_id, profiles:worker_id(full_name)')
        .eq('crew_leader_id', profile.id).eq('work_date', today),
    ])
    const jobs = jobsRes.data || []
    const segmentedIds = jobs.filter(j => j.has_segments).map(j => j.id)
    if (segmentedIds.length > 0) {
      const { data: segs } = await supabase.from('job_segments').select('*').in('job_id', segmentedIds)
      const byJob = {}
      ;(segs || []).forEach(s => { (byJob[s.job_id] = byJob[s.job_id] || []).push(s) })
      jobs.forEach(j => { j.segments = sortSegments(byJob[j.id] || []) })
    }
    setJobs(jobs)
    setActiveEntry(activeRes.data || null)
    setTodayEntries(entriesRes.data || [])
    setCrew(crewRes.data || [])
    autoStopFiredRef.current = false
    setLoading(false)
  }, [profile?.id, today, todayMidnightISO])

  useEffect(() => { load() }, [load])

  // Live tick while a job is running
  useEffect(() => {
    if (!activeEntry) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [activeEntry?.id])

  // Auto-stop after 12 hours
  useEffect(() => {
    if (!activeEntry || autoStopFiredRef.current) return
    const started = new Date(activeEntry.clock_in).getTime()
    if (Date.now() - started >= AUTO_STOP_MS) {
      autoStopFiredRef.current = true
      const cap = new Date(started + AUTO_STOP_MS).toISOString()
      stopJob(null, cap, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [now, activeEntry])

  const startJob = async (jobId) => {
    if (busy) return
    // Capture timestamp IMMEDIATELY, before any await
    const startISO = new Date().toISOString()
    const prev = activeEntry
    setBusy(true); setError(null)
    try {
      if (prev) {
        const stopISO = startISO
        const hrs = Math.round(((new Date(stopISO) - new Date(prev.clock_in)) / 3600000) * 100) / 100
        const { error: stopErr } = await supabase.from('job_time_entries')
          .update({ clock_out: stopISO, hours_worked: hrs })
          .eq('id', prev.id)
        if (stopErr) throw stopErr
      }
      const { data, error: insErr } = await supabase.from('job_time_entries')
        .insert({ job_id: jobId, user_id: profile.id, clock_in: startISO })
        .select().single()
      if (insErr) throw insErr
      setActiveEntry(data)
      await load()
    } catch (err) {
      setError('Could not start timer: ' + err.message)
    }
    setBusy(false)
  }

  const stopJob = async (noteText, stopISOOverride, autoStopped) => {
    if (!activeEntry || busy) return
    // Capture timestamp IMMEDIATELY, before any await
    const stopISO = stopISOOverride || new Date().toISOString()
    const entry = activeEntry
    const hrs = Math.round(((new Date(stopISO) - new Date(entry.clock_in)) / 3600000) * 100) / 100
    setActiveEntry(null)
    setBusy(true); setError(null)
    try {
      const { error: upErr } = await supabase.from('job_time_entries')
        .update({ clock_out: stopISO, hours_worked: hrs, note: noteText || null })
        .eq('id', entry.id)
      if (upErr) throw upErr
      await load()
      if (autoStopped) alert('Timer auto-stopped after 12 hours. Please check the time entry.')
    } catch (err) {
      setError('Could not save stop time: ' + err.message)
      // Try to restore running state so user can retry
      setActiveEntry(entry)
    }
    setBusy(false)
  }

  const cancelJob = async () => {
    if (!activeEntry || busy) return
    const entryId = activeEntry.id
    setBusy(true); setError(null)
    setActiveEntry(null)
    try {
      await supabase.from('job_time_entries').delete().eq('id', entryId)
    } catch (err) { setError('Could not cancel: ' + err.message) }
    setBusy(false)
  }

  const openMaps = (job) => {
    const address = [job.site_address, job.site_city, job.site_state].filter(Boolean).join(', ')
    if (address) window.open(`https://maps.google.com/?q=${encodeURIComponent(address)}`, '_blank')
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}><div style={styles.title}>MY DAY</div></div>
        <div style={styles.empty}>Loading your schedule...</div>
      </div>
    )
  }

  const segmentedStartOf = (j) => {
    if (!j.has_segments || !j.segments?.length) return j.start_date || ''
    const rel = pickRelevantSegment(j.segments, today)
    return rel?.start_date || j.start_date || ''
  }
  const segmentedEndOf = (j) => {
    if (!j.has_segments || !j.segments?.length) return j.end_date || j.start_date || ''
    const rel = pickRelevantSegment(j.segments, today)
    return rel?.end_date || rel?.start_date || j.end_date || ''
  }
  const todayJobs = jobs.filter(j => {
    const start = segmentedStartOf(j)
    const end = segmentedEndOf(j)
    return start && start <= today && (!end || end >= today)
  })
  const upcomingJobs = jobs.filter(j => segmentedStartOf(j) > today).slice(0, 5)
  const activeJobId = activeEntry?.job_id
  const crewNames = crew.map(c => c.profiles?.full_name).filter(Boolean)
  const elapsedSeconds = activeEntry ? Math.floor((now - new Date(activeEntry.clock_in).getTime()) / 1000) : 0
  const cancelAllowed = activeEntry ? (Date.now() - new Date(activeEntry.clock_in).getTime() < CANCEL_WINDOW_MS) : false

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.title}>MY DAY</div>
        <div style={styles.dateLabel}>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</div>
      </div>

      {showCrewPicker && (
        <div style={styles.modalOverlay} onClick={() => setShowCrewPicker(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>UPDATE CREW</div>
              <button onClick={() => setShowCrewPicker(false)} style={styles.modalClose}>✕</button>
            </div>
            <div style={styles.modalBody}>
              <CrewSelection crewLeaderId={profile.id} compact onSaved={() => { setShowCrewPicker(false); load() }} />
            </div>
          </div>
        </div>
      )}

      <div style={styles.crewHeader}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={styles.crewLabel}>MY CREW — {crew.length} {crew.length === 1 ? 'WORKER' : 'WORKERS'}</div>
          <div style={styles.crewList}>
            {crewNames.length === 0 ? <span style={styles.crewEmpty}>No crew selected — tap Switch Crew</span> : crewNames.join(' · ')}
          </div>
        </div>
        <button onClick={() => setShowCrewPicker(true)} style={styles.switchCrewBtn}>SWITCH CREW</button>
      </div>

      {error && <div style={styles.error}>{error} <button onClick={() => setError(null)} style={styles.errorClose}>dismiss</button></div>}

      {todayJobs.length === 0 ? (
        <div style={styles.emptyCard}>No active jobs assigned to you today.</div>
      ) : (
        <div style={styles.section}>
          <div style={styles.sectionLabel}>TODAY · {todayJobs.length} JOB{todayJobs.length !== 1 ? 'S' : ''} — IN PRIORITY ORDER</div>
          {todayJobs.map((job, idx) => {
            const seg = job.has_segments ? pickRelevantSegment(job.segments, today) : null
            return (
              <JobStop
                key={job.id}
                job={job}
                segment={seg}
                segmentCount={job.segments?.length || 0}
                index={idx + 1}
                isToday
                isActive={activeJobId === job.id}
                elapsedSeconds={elapsedSeconds}
                cancelAllowed={cancelAllowed}
                busy={busy}
                hasOtherActive={!!activeJobId && activeJobId !== job.id}
                finishedEntry={todayEntries.filter(e => e.job_id === job.id).sort((a, b) => (b.clock_out || '').localeCompare(a.clock_out || ''))[0]}
                crewNames={crewNames}
                onNavigate={openMaps}
                onStart={() => startJob(job.id)}
                onStop={(note) => stopJob(note)}
                onCancel={cancelJob}
              />
            )
          })}
        </div>
      )}

      {upcomingJobs.length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionLabel}>COMING UP</div>
          {upcomingJobs.map((job, idx) => {
            const seg = job.has_segments ? pickRelevantSegment(job.segments, today) : null
            return (
              <JobStop
                key={job.id}
                job={job}
                segment={seg}
                segmentCount={job.segments?.length || 0}
                index={idx + 1}
                onNavigate={openMaps}
                readOnly
              />
            )
          })}
        </div>
      )}
    </div>
  )
}

function JobStop({ job, segment, segmentCount, index, isToday, readOnly, isActive, elapsedSeconds, cancelAllowed, busy, hasOtherActive, finishedEntry, crewNames, onNavigate, onStart, onStop, onCancel }) {
  const bizColor = getBizColor(job.business_id)
  const isAboveGround = job.business_id === 'pools' && job.job_type === 'above_ground'
  const lines = parseLines(job)
  const flags = [
    job.has_heater && 'HEATER',
    job.has_main_drain && 'MAIN DRAIN',
    job.has_deep_end && 'DEEP END',
    job.dirt_haul_away && 'DIRT HAUL AWAY',
    job.sand_delivery && 'SAND DELIVERY',
    job.water_truck_time && 'WATER TRUCK',
    job.has_deck && 'DECK',
  ].filter(Boolean)

  return (
    <div style={{ ...styles.stopCard, borderLeft: `4px solid ${bizColor}`, opacity: readOnly ? 0.7 : 1 }}>
      <div style={styles.stopHeader}>
        <div style={styles.stopNum}>{isToday ? `STOP ${index}` : job.start_date}</div>
        <button onClick={() => onNavigate(job)} style={{ ...styles.navBtn, backgroundColor: bizColor }}>NAVIGATE →</button>
      </div>
      <div style={styles.clientName}>{job.client_name}</div>
      {segment && (
        <div style={{ marginBottom: '8px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '4px 10px', backgroundColor: 'rgba(74,140,106,0.12)', border: '1px solid rgba(74,140,106,0.3)', borderRadius: '3px' }}>
            <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', letterSpacing: '1.5px', color: '#4a8c6a', fontWeight: 700 }}>VISIT {segment.segment_number}/{segmentCount}</span>
            <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
              {segment.start_date}{segment.end_date && segment.end_date !== segment.start_date ? ' → ' + segment.end_date : ''}
            </span>
          </div>
          {segment.description && <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.75)', marginTop: '4px' }}>{segment.description}</div>}
        </div>
      )}
      <div style={styles.address}>📍 {job.site_address}{job.site_city ? ', ' + job.site_city : ''}</div>
      <div style={styles.metaRow}>
        {job.job_type && (
          <span style={{ ...styles.jobTypeBadge, color: bizColor, borderColor: bizColor + '40' }}>
            {job.job_type.replace(/_/g, ' ').toUpperCase()}
          </span>
        )}
        {job.pool_dimensions && <span style={styles.metaPill}>{job.pool_dimensions}</span>}
        {job.pool_type && <span style={styles.metaPill}>{job.pool_type}</span>}
        {job.acreage ? <span style={styles.metaPill}>{job.acreage} ac</span> : null}
      </div>
      {flags.length > 0 && (
        <div style={styles.flagsRow}>
          {flags.map(flag => <div key={flag} style={styles.flag}>{flag}</div>)}
        </div>
      )}
      {job.water_truck_time && (
        <div style={styles.detailRow}>
          <span style={styles.detailLabel}>WATER TRUCK</span>
          <span style={styles.detailValue}>{job.water_truck_company} — {job.water_truck_time}</span>
        </div>
      )}
      {job.sand_delivery && job.sand_delivery_time && (
        <div style={styles.detailRow}>
          <span style={styles.detailLabel}>SAND DELIVERY</span>
          <span style={styles.detailValue}>{job.sand_delivery_company} — {job.sand_delivery_time}</span>
        </div>
      )}
      {job.dirt_haul_away && job.dirt_haul_notes && (
        <div style={styles.detailRow}>
          <span style={styles.detailLabel}>HAUL AWAY</span>
          <span style={styles.detailValue}>{job.dirt_haul_notes}</span>
        </div>
      )}
      {lines.length > 0 && !readOnly && (
        <div style={styles.linesWrap}>
          <div style={styles.linesLabel}>LINE ITEMS</div>
          {lines.map((ln, i) => (
            <div key={i} style={styles.lineRow}>
              <div style={styles.lineDesc}>
                {ln.description || ln.category || 'Item'}
                {ln.quantity ? <span style={styles.lineQty}> × {ln.quantity}{ln.unit ? ' ' + ln.unit : ''}</span> : null}
              </div>
              {ln.total_cost != null && <div style={styles.linePrice}>{money(ln.total_cost)}</div>}
            </div>
          ))}
        </div>
      )}
      {isAboveGround && (job.contract_value || job.due_on_site) && (() => {
        const due = resolveDueOnSite(job)
        return (
          <div style={styles.paymentFlag}>
            💰 COLLECT ${Number(due).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ON SITE
            {job.due_on_site_notes && <div style={{ fontSize: '12px', fontWeight: 400, color: '#b48c3c', opacity: 0.8, marginTop: '4px' }}>{job.due_on_site_notes}</div>}
          </div>
        )
      })()}
      {job.notes && <div style={styles.notes}>{job.notes}</div>}
      {!readOnly && (
        <JobTimer
          isActive={isActive}
          busy={busy}
          disabled={!isActive && hasOtherActive && false /* starting here auto-stops the other */}
          elapsedSeconds={elapsedSeconds}
          cancelAllowed={cancelAllowed}
          finishedEntry={finishedEntry}
          crewNames={crewNames}
          onStart={onStart}
          onStop={onStop}
          onCancel={onCancel}
        />
      )}
    </div>
  )
}

const styles = {
  container: { fontFamily: "'Barlow',sans-serif", maxWidth: '680px' },
  header: { marginBottom: '20px' },
  title: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '28px', fontWeight: 700, letterSpacing: '3px', color: '#ffffff' },
  dateLabel: { fontSize: '15px', color: 'rgba(255,255,255,0.35)', marginTop: '4px' },
  empty: { padding: '60px', textAlign: 'center', color: 'rgba(255,255,255,0.25)', fontSize: '15px' },
  emptyCard: { padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.25)', fontSize: '15px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '4px' },
  section: { marginBottom: '28px' },
  sectionLabel: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', letterSpacing: '3px', color: 'rgba(255,255,255,0.3)', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.05)' },
  stopCard: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', padding: '20px', marginBottom: '12px' },
  stopHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
  stopNum: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)' },
  navBtn: { border: 'none', borderRadius: '3px', padding: '10px 16px', color: '#0a0f1a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' },
  clientName: { fontSize: '22px', fontWeight: 600, color: '#ffffff', marginBottom: '6px' },
  address: { fontSize: '14px', color: 'rgba(255,255,255,0.5)', marginBottom: '12px' },
  metaRow: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px', alignItems: 'center' },
  jobTypeBadge: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', letterSpacing: '1.5px', padding: '4px 10px', borderRadius: '3px', border: '1px solid', display: 'inline-block' },
  metaPill: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', letterSpacing: '1px', padding: '3px 9px', borderRadius: '2px', backgroundColor: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.08)' },
  flagsRow: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' },
  flag: { backgroundColor: 'rgba(180,140,60,0.1)', border: '1px solid rgba(180,140,60,0.25)', borderRadius: '3px', padding: '4px 10px', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', letterSpacing: '1px', color: '#b48c3c' },
  detailRow: { display: 'flex', gap: '12px', marginBottom: '8px', alignItems: 'flex-start' },
  detailLabel: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.3)', flexShrink: 0, paddingTop: '2px', minWidth: '90px' },
  detailValue: { fontSize: '14px', color: 'rgba(255,255,255,0.7)' },
  linesWrap: { marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.05)' },
  linesLabel: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '8px' },
  lineRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.03)' },
  lineDesc: { flex: 1, minWidth: 0, fontSize: '13px', color: 'rgba(255,255,255,0.75)' },
  lineQty: { color: 'rgba(255,255,255,0.4)', marginLeft: '6px' },
  linePrice: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '13px', color: '#b48c3c', letterSpacing: '1px', flexShrink: 0 },
  paymentFlag: { backgroundColor: 'rgba(180,140,60,0.1)', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '3px', padding: '12px 16px', fontSize: '15px', fontWeight: 600, color: '#b48c3c', marginTop: '12px' },
  notes: { fontSize: '13px', color: 'rgba(255,255,255,0.4)', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.05)', lineHeight: 1.6 },
  crewHeader: { display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', backgroundColor: 'rgba(180,140,60,0.06)', border: '1px solid rgba(180,140,60,0.2)', borderRadius: '4px', marginBottom: '18px' },
  crewLabel: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', letterSpacing: '2px', color: '#b48c3c', marginBottom: '4px' },
  crewList: { fontSize: '14px', color: 'rgba(255,255,255,0.8)', lineHeight: 1.4, wordBreak: 'break-word' },
  crewEmpty: { color: 'rgba(255,255,255,0.35)', fontStyle: 'italic' },
  switchCrewBtn: { padding: '8px 14px', backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', color: '#0a0f1a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer', flexShrink: 0 },
  modalOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  modal: { backgroundColor: '#0d1420', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', width: '100%', maxWidth: '640px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  modalHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  modalTitle: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '16px', fontWeight: 700, letterSpacing: '2px', color: '#b48c3c' },
  modalClose: { backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.4)', fontSize: '14px', cursor: 'pointer', padding: '4px 10px' },
  modalBody: { flex: 1, overflowY: 'auto', padding: '16px 20px' },
  error: { padding: '12px 16px', marginBottom: '16px', backgroundColor: 'rgba(224,92,92,0.08)', border: '1px solid rgba(224,92,92,0.3)', borderRadius: '4px', color: '#ff8888', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' },
  errorClose: { backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', borderRadius: '3px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px' },
}
